/**
 * Toolbar control to start or join a real-time collaboration session on the
 * current document. Wraps {@link useDocumentCollaboration}: "Start a room"
 * creates a relay room and shows a short code; entering a code joins an existing
 * one. Both drive the Loro sync loop over `/api/collab`.
 */
import { createSignal, Show } from "solid-js";
import { Users2 } from "@fleet/icons";
import { msg } from "@lingui/core/macro";
import type { Editor } from "@tiptap/core";
import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useDocumentCollaboration } from "../hooks/use-document-collaboration";

export function CollaborateButton(props: { editor: () => Editor | null }) {
  const { t } = useI18n();
  // oxlint-disable-next-line solid/reactivity -- the accessor itself is handed to the hook, which tracks it
  const { session, start, stop } = useDocumentCollaboration(props.editor);
  const [joinCode, setJoinCode] = createSignal("");

  return (
    <Popover>
      <PopoverTrigger
        as={Button}
        variant="ghost"
        size="sm"
        class="shrink-0 gap-1.5"
        data-testid="collaborate-open"
        aria-label={t(msg`Collaborate`)}
        title={t(msg`Collaborate`)}
      >
        <Users2 class="h-4 w-4" classList={{ "text-accent": session.status() === "connected" }} />
        <span class="hidden sm:inline">{t(msg`Collaborate`)}</span>
      </PopoverTrigger>
      <PopoverContent class="w-72 space-y-3 p-4">
        <Show
          when={session.status() === "connected" && session.code()}
          fallback={
            <div class="space-y-3">
              <Button
                variant="secondary"
                size="sm"
                class="w-full"
                data-testid="collaborate-start"
                disabled={session.status() === "connecting"}
                onClick={() => void start()}
              >
                {t(msg`Start a room`)}
              </Button>
              <div class="flex gap-2">
                <input
                  type="text"
                  value={joinCode()}
                  onInput={(e) => setJoinCode(e.currentTarget.value.toUpperCase())}
                  placeholder={t(msg`Enter a code`)}
                  data-testid="collaborate-join-input"
                  class="w-full rounded-lg border border-border bg-transparent px-2 py-1 text-sm outline-none focus-visible:ring-2 focus-visible:ring-accent"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="collaborate-join"
                  disabled={joinCode().length === 0 || session.status() === "connecting"}
                  onClick={() => void start(joinCode())}
                >
                  {t(msg`Join`)}
                </Button>
              </div>
            </div>
          }
        >
          <div class="space-y-2">
            <p class="text-xs text-muted-foreground">{t(msg`Share this code to collaborate:`)}</p>
            <p
              class="select-all text-center font-mono text-lg font-semibold tracking-widest"
              data-testid="collaborate-code"
            >
              {session.code()}
            </p>
            <Button
              variant="ghost"
              size="sm"
              class="w-full"
              data-testid="collaborate-stop"
              onClick={() => stop()}
            >
              {t(msg`Stop collaborating`)}
            </Button>
          </div>
        </Show>
      </PopoverContent>
    </Popover>
  );
}
